import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  message,
  Space,
  Modal,
  Form,
  Select,
  Tooltip,
  Row,
  Tag,
  Empty
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  UndoOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import { membershipService } from '../../../services';
import {
  ActualProductsTabProps,
  MembershipProductCreateItem,
  MembershipProductDeleteItem,
  MembershipProductUpdateItem,
  ProductData
} from "../../../model";
import {productService} from "../../../services/domain/product/service";
import {ExtendedProductResponse} from "../../../model/props/changeFlag";

const { confirm } = Modal;

const ActualProductsTab: React.FC<ActualProductsTabProps> = ({
  membershipId,
  onDataChanged,
  changedFields,
  onSaveSuccess,
  isEditing,
}) => {
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState<ExtendedProductResponse[]>([]);
  const [productData, setProductData] = useState<ExtendedProductResponse[]>([]);
  const [modifiedProducts, setModifiedProducts] = useState<{[key: number]: boolean}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<ProductData[]>([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<ExtendedProductResponse | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const userAccount = localStorage.getItem('userAccount') ?? '';

  // 載入產品資料
  const fetchProductData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await membershipService.getProducts({ membershipId });
      if (data && Array.isArray(data)) {
        setProductData(data);
        setOriginalData(JSON.parse(JSON.stringify(data))); // 深拷貝保存原始資料
        setModifiedProducts({});
        setHasChanges(false);
        // 通知父元件已無變更
        onDataChanged('products', false);
      } else {
        setProductData([]);
        setOriginalData([]);
        message.info('沒有找到產品資料');
      }
    } catch (error) {
      console.error('Failed to fetch product data:', error);
      message.error('載入產品資料時發生錯誤');
      setProductData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  }, [membershipId, onDataChanged]);

  const fetchAvailableProducts = useCallback(async () => {
    try {
      // 使用 productService 取得可用產品清單
      const productData = await productService.search({});
      
      if (productData && productData.length > 0) {
        // 將 API 回傳的產品資料轉換成我們需要的格式
        const formattedProducts: ProductData[] = productData.map(product => ({
          id: product.productId,
          name: product.productName,
          code: product.productCode
        }));
        
        setAvailableProducts(formattedProducts);
      }
    } catch (error) {
      console.error('Failed to fetch available products:', error);
      message.error('載入可用產品時發生錯誤');
    }
  }, []);

  useEffect(() => {
    fetchProductData();
    fetchAvailableProducts();
  }, [fetchProductData, fetchAvailableProducts]);

  // 判斷產品是否已修改
  const isProductModified = (record: ExtendedProductResponse) => {
    return !!modifiedProducts[record.membershipProductId];
  };
  
  // 產品是否被標記為刪除
  const isProductDeleted = (record: ExtendedProductResponse) => {
    return !!record._deleted;
  };
  
  // 產品是否為新增
  const isProductNew = (record: ExtendedProductResponse) => {
    return record.membershipProductId < 0 || !!record._new;
  };

  // 新增產品
  const handleAddProduct = async () => {
    if (!isEditing) return;
    
    try {
      const values = await addForm.validateFields();
      
      // 檢查是否已經存在相同產品
      const isDuplicate = productData.some(item => 
        item.productId === values.productId && !item._deleted
      );
      
      if (isDuplicate) {
        message.error('此產品已存在，請選擇其他產品');
        return;
      }
      
      // 將新產品加入至暫存資料中
      const selectedProduct = availableProducts.find(p => p.id === values.productId);
      if (selectedProduct) {
        const newProduct: ExtendedProductResponse = {
          membershipProductId: -Date.now(), // 臨時负值 ID，用來識別新增項目
          productId: selectedProduct.id,
          productCode: selectedProduct.code,
          productName: selectedProduct.name,
          _new: true
        };
        
        const newData = [...productData, newProduct];
        setProductData(newData);
        
        // 標記為已修改
        setModifiedProducts(prev => ({
          ...prev,
          [newProduct.membershipProductId]: true
        }));
        setHasChanges(true);
        
        // 通知上層組件有資料變更
        onDataChanged('products', true);
        
        // setIsAddModalVisible(false);
        addForm.resetFields();
        message.success('已添加產品，點擊儲存按鈕以儲存變更');
      }
    } catch (error) {
      console.error('Add product failed:', error);
      message.error('新增產品時發生錯誤');
    }
  };

  // 更新產品
  const handleUpdateProduct = async () => {
    if (!currentProduct || !isEditing) return;
    
    try {
      const values = await editForm.validateFields();
      
      // 檢查是否已存在相同產品
      const isDuplicate = productData.some(item => 
        item.productId === values.productId && 
        item.membershipProductId !== currentProduct.membershipProductId &&
        !item._deleted
      );
      
      if (isDuplicate) {
        message.error('此產品已存在，請選擇其他產品');
        return;
      }
      
      // 在暫存資料中更新產品
      const selectedProduct = availableProducts.find(p => p.id === values.productId);
      if (selectedProduct) {
        const newData = productData.map(item => {
          if (item.membershipProductId === currentProduct.membershipProductId) {
            return {
              ...item,
              productId: selectedProduct.id,
              productCode: selectedProduct.code,
              productName: selectedProduct.name
            };
          }
          return item;
        });
        
        setProductData(newData);
        
        // 標記為已修改
        setModifiedProducts(prev => ({
          ...prev,
          [currentProduct.membershipProductId]: true
        }));
        setHasChanges(true);
        
        // 通知上層組件有資料變更
        onDataChanged('products', true);
        
        setIsEditModalVisible(false);
        setCurrentProduct(null);
        editForm.resetFields();
        message.success('已更新產品，點擊儲存按鈕以儲存變更');
      }
    } catch (error) {
      console.error('Update product failed:', error);
      message.error('更新產品時發生錯誤');
    }
  };

  // 刪除產品
  const handleDeleteProduct = (record: ExtendedProductResponse) => {
    if (!isEditing) return;
    
    // 如果是新增的項目（负數 ID）
    if (record.membershipProductId < 0) {
      // 直接從暫存資料中移除
      setProductData(productData.filter(item => item.membershipProductId !== record.membershipProductId));
      
      // 從修改記錄中移除
      const updatedModified = { ...modifiedProducts };
      delete updatedModified[record.membershipProductId];
      setModifiedProducts(updatedModified);
      
      // 更新變更狀態
      const hasRemainingChanges = Object.keys(updatedModified).length > 0;
      setHasChanges(hasRemainingChanges);
      onDataChanged('products', hasRemainingChanges);
      
      message.success('已移除產品');
      return;
    }
    
    // 將現有產品標記為待刪除
    setProductData(prevData => 
      prevData.map(item => 
        item.membershipProductId === record.membershipProductId
          ? { ...item, _deleted: true }
          : item
      )
    );
    
    // 標記為已修改
    setModifiedProducts(prev => ({
      ...prev,
      [record.membershipProductId]: true
    }));
    setHasChanges(true);
    
    // 通知上層組件有資料變更
    onDataChanged('products', true);
    
    message.success('已標記產品為刪除，點擊儲存按鈕以完成刪除');
  };

  // 確認刪除產品
  const confirmDelete = (record: ExtendedProductResponse) => {
    if (!isEditing) return;
    
    confirm({
      title: '確定要刪除此產品嗎？',
      icon: <ExclamationCircleFilled />,
      content: `將會移除產品：${record.productName} (${record.productCode})`,
      okText: '確定',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        handleDeleteProduct(record);
      },
    });
  };

  // 開啟編輯視窗
  const showEditModal = (record: ExtendedProductResponse) => {
    if (!isEditing) return;
    
    setCurrentProduct(record);
    editForm.setFieldsValue({
      productId: record.productId,
    });
    setIsEditModalVisible(true);
  };
  
  // 儲存所有變更
  const handleSaveChanges = async () => {
    if (!hasChanges || !isEditing) {
      message.info('沒有需要儲存的變更');
      return;
    }
    
    setLoading(true);
    
    try {
      // 備份原本資料，用於失敗時還原
      const originalProductDataBackup = JSON.parse(JSON.stringify(productData));
      
      // 准備要新增的產品
      const productsToAdd = productData
        .filter(p => p.membershipProductId < 0 || p._new) // 新增的產品有臨時负值ID或_new標記
        .map(p => ({
          membershipId,
          productId: p.productId,
          createdBy: userAccount
        }) as MembershipProductCreateItem);
      
      // 准備要更新的產品
      const productsToUpdate = productData
        .filter(p => p.membershipProductId > 0 && // 正常 ID
                  modifiedProducts[p.membershipProductId] && // 已修改 
                  !p._deleted && // 不是待刪除
                  !p._new) // 不是新增項目
        .map(p => ({
          membershipPorductId: p.membershipProductId,
          membershipId,
          productId: p.productId,
          modifiedBy: userAccount,
        }) as MembershipProductUpdateItem);
      
      // 准備要刪除的產品
      const productsToDelete = productData
        .filter(p => p.membershipProductId > 0 && p._deleted) // 被標記為刪除的現有產品
        .map(p => ({
          membershipPorductId: p.membershipProductId,
        }) as MembershipProductDeleteItem);
      
      // 执行 API 請求
      let isSuccess = true;
      let errorMsg = '';
      
      // 新增產品
      if (productsToAdd.length > 0) {
        try {
          const createResponse = await membershipService.createProducts({
            membershipProducts: productsToAdd
          });
          
          if (!createResponse || createResponse.createdCount === 0) {
            isSuccess = false;
            errorMsg = '新增產品失敗';
          }
        } catch (error) {
          console.error('Create products failed:', error);
          isSuccess = false;
          errorMsg = '新增產品時發生錯誤';
        }
      }
      
      // 更新產品
      if (isSuccess && productsToUpdate.length > 0) {
        try {
          const updateResponse = await membershipService.updateProducts({
            membershipProducts: productsToUpdate
          });
          
          if (!updateResponse || updateResponse.updatedCount === 0) {
            isSuccess = false;
            errorMsg = '更新產品失敗';
          }
        } catch (error) {
          console.error('Update products failed:', error);
          isSuccess = false;
          errorMsg = '更新產品時發生錯誤';
        }
      }
      
      // 刪除產品
      if (isSuccess && productsToDelete.length > 0) {
        try {
          const deleteResponse = await membershipService.deleteProducts({
            membershipPorductIds: productsToDelete,
            modifiedBy: userAccount
          });
          
          if (!deleteResponse || deleteResponse.deletedCount === 0) {
            isSuccess = false;
            errorMsg = '刪除產品失敗';
          }
        } catch (error) {
          console.error('Delete products failed:', error);
          isSuccess = false;
          errorMsg = '刪除產品時發生錯誤';
        }
      }
      
      if (isSuccess) {
        message.success('所有產品變更已儲存成功');
        // 重新載入最新資料
        fetchProductData();
        onSaveSuccess();
      } else {
        // 還原原始資料
        setProductData(originalProductDataBackup);
        message.error(errorMsg || '儲存產品變更失敗');
      }
    } catch (error) {
      console.error('Save products failed:', error);
      message.error('儲存產品變更時發生錯誤');
      // 還原原始資料
      handleResetChanges();
    } finally {
      setLoading(false);
    }
  };
  
  // 重置變更
  const handleResetChanges = () => {
    setProductData(JSON.parse(JSON.stringify(originalData)));
    setModifiedProducts({});
    setHasChanges(false);
    onDataChanged('products', false);
    message.info('已返回原始資料');
  };

  // 獲取表格欄位定義
  const getColumns = () => {
    // 基本欄位
    const baseColumns = [
      {
        title: '產品代號',
        dataIndex: 'productCode',
        key: 'productCode',
        render: (text: string, record: ExtendedProductResponse) => {
          if (isProductDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text}</span>;
          }
          return isProductModified(record) ? 
            <span style={{ color: '#f50' }}>{text}</span> : 
            text;
        }
      },
      {
        title: '工廠實際生產之產品',
        dataIndex: 'productName',
        key: 'productName',
        render: (text: string, record: ExtendedProductResponse) => {
          if (isProductDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text}</span>;
          }
          return isProductModified(record) ? 
            <span style={{ color: '#f50' }}>{text}</span> : 
            text;
        }
      },
      {
        title: '產品英文名稱',
        dataIndex: 'productNameEn',
        key: 'productNameEn',
        render: (text: string, record: ExtendedProductResponse) => {
          if (isProductDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text || '-'}</span>;
          }
          return isProductModified(record) ? 
            <span style={{ color: '#f50' }}>{text || '-'}</span> : 
            (text || '-');
        }
      }
    ];
    
    // 只在編輯模式下添加額外的欄位
    if (isEditing) {
      return [
        ...baseColumns,
        {
          title: '狀態',
          key: 'status',
          width: 100,
          render: (_: any, record: ExtendedProductResponse) => {
            if (isProductDeleted(record)) {
              return <Tag color="red">待刪除</Tag>;
            }
            if (isProductNew(record)) {
              return <Tag color="green">新增</Tag>;
            }
            if (isProductModified(record)) {
              return <Tag color="orange">已修改</Tag>;
            }
            return null;
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          render: (_: any, record: ExtendedProductResponse) => {
            if (isProductDeleted(record)) {
              return (
                <Button 
                  type="link" 
                  onClick={() => {
                    // 取消刪除標記
                    setProductData(prevData => 
                      prevData.map(item => 
                        item.membershipProductId === record.membershipProductId
                          ? { ...item, _deleted: false }
                          : item
                      )
                    );
                    // 使用時間戳創建新的修改記錄，以確保觸發狀態更新
                    setModifiedProducts(prev => {
                      const newModified = { ...prev };
                      delete newModified[record.membershipProductId];
                      return newModified;
                    });
                    // 更新變更狀態
                    setHasChanges(true);
                    onDataChanged('products', true);
                  }}
                >
                  取消刪除
                </Button>
              );
            }
            
            return (
              <Space size="small">
                <Tooltip title="編輯">
                  <Button 
                    type="text" 
                    icon={<EditOutlined />} 
                    onClick={() => showEditModal(record)} 
                    disabled={isProductDeleted(record)}
                  />
                </Tooltip>
                <Tooltip title="刪除">
                  <Button 
                    type="text" 
                    icon={<DeleteOutlined />} 
                    danger 
                    onClick={() => confirmDelete(record)}
                    disabled={isProductDeleted(record)}
                  />
                </Tooltip>
              </Space>
            );
          },
        }
      ];
    }
    
    return baseColumns;
  };

  const renderTable = () => {
    if (!productData || productData.length === 0) {
      return (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
          description="尚無產品資料" 
        />
      );
    }
    
    return (
      <Table
        sticky
        rowKey="membershipProductId"
        columns={getColumns()}
        dataSource={productData.filter(item => !isProductDeleted(item) || isEditing)}
        loading={loading}
        pagination={false}
        rowClassName={(record) => isProductModified(record) ? 'modified-row' : ''}
      />
    );
  };

  return (
    <Card>
      {isEditing && (
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setIsAddModalVisible(true)}
            >
              新增產品
            </Button>
          </Space>
          
          <Space>
            <Button 
              icon={<UndoOutlined />} 
              onClick={handleResetChanges}
              disabled={!hasChanges}
            >
              重置
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={handleSaveChanges}
              loading={loading}
              disabled={!hasChanges}
            >
              儲存變更
            </Button>
          </Space>
        </Row>
      )}

      {renderTable()}

      {/* 新增產品視窗 */}
      <Modal
        title="新增產品"
        open={isAddModalVisible}
        onOk={handleAddProduct}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        okText="新增"
        cancelText="關閉"
        confirmLoading={loading}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="productId"
            label="選擇產品"
            rules={[{ required: true, message: '請選擇產品' }]}
          >
            <Select 
              placeholder="請選擇產品"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => {
                // 安全地获取选项的文本内容
                const optionLabel = option?.label ? String(option.label) : '';
                return optionLabel.toLowerCase().includes(input.toLowerCase());
              }}
              options={availableProducts.map(product => ({
                value: product.id,
                label: `${product.name} (${product.code})`
              }))}
            >
              {/* 选项通过options属性传递 */}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 編輯產品視窗 */}
      <Modal
        title="編輯產品"
        open={isEditModalVisible}
        onOk={handleUpdateProduct}
        onCancel={() => {
          setIsEditModalVisible(false);
          setCurrentProduct(null);
          editForm.resetFields();
        }}
        okText="更新"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="productId"
            label="選擇產品"
            rules={[{ required: true, message: '請選擇產品' }]}
          >
            <Select 
              placeholder="請選擇產品"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => {
                // 安全地获取选项的文本内容
                const optionLabel = option?.label ? String(option.label) : '';
                return optionLabel.toLowerCase().includes(input.toLowerCase());
              }}
              options={availableProducts.map(product => ({
                value: product.id,
                label: `${product.name} (${product.code})`
              }))}
            >
              {/* 选项通过options属性传递 */}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ActualProductsTab;
